/**
 * Renders the `choice` core view (ported from the MEIS-13490 prototype's
 * `AgentChoiceView.tsx`): a structured question form — a radio (single
 * select) or checkbox (multi select) per option, "Other" as the last row,
 * and nothing is sent before the send button (an earlier version sent at
 * once on a single click, leaving no room to correct a misclick).
 *
 * Once this view's message is no longer the newest one, `AgentChatBox`
 * passes `answeredWith` (the following user message's text) and this
 * renders read-only instead: the question plus one chip per option, the
 * options the answer named (`chosenOptionLabels` in `agentChoiceSpec.ts`)
 * filled, the rest outlined — no radios/checkboxes/Send button, matching
 * the prototype's read-only rendering.
 */
import { Check as CheckIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  buildChoiceAnswerText,
  chosenOptionLabels,
  validateChoiceView,
  type ChoiceViewSpec,
} from "./agentChoiceSpec.js";
import type { AgentViewComponentProps } from "./registry.js";

// Not a valid option label, so it can never collide with one.
const OTHER = "\u0000other";

export function AgentChoiceView({
  view,
  onSend,
  busy,
  answeredWith,
}: AgentViewComponentProps) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const [checked, setChecked] = React.useState<Set<string>>(new Set());
  const [otherText, setOtherText] = React.useState("");

  const problems = validateChoiceView(view);
  if (problems.length > 0)
    return (
      <Alert severity="warning" sx={{ py: 0, my: 0.5 }}>
        {t("Choice could not be shown")}
      </Alert>
    );

  // Safe: `validateChoiceView` above checked every field this type relies on.
  const spec = view as ChoiceViewSpec;

  if (answeredWith !== undefined) {
    const chosen = chosenOptionLabels(spec, answeredWith);
    return (
      <Stack spacing={0.5} sx={{ my: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {spec.question}
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
        >
          {spec.options.map(option => {
            const isChosen = chosen.has(option.label);
            return (
              <Chip
                key={option.label}
                size="small"
                label={option.label}
                variant={isChosen ? "filled" : "outlined"}
                color={isChosen ? "primary" : "default"}
                icon={isChosen ? <CheckIcon fontSize="small" /> : undefined}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  }

  const isChecked = (key: string) =>
    spec.multiSelect
      ? checked.has(key)
      : checked.has(key) && checked.size === 1;
  const pick = (key: string) =>
    setChecked(current => {
      if (!spec.multiSelect) return new Set([key]);
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const orderedChecked = spec.options
    .filter(option => checked.has(option.label))
    .map(option => option.label);
  const otherPicked = checked.has(OTHER);
  const trimmedOther = otherPicked ? otherText.trim() : "";
  const canSend =
    (orderedChecked.length > 0 && !(otherPicked && !spec.multiSelect)) ||
    trimmedOther !== "";
  const submit = () => {
    if (!canSend || busy) return;
    onSend(
      buildChoiceAnswerText(
        spec.multiSelect || !otherPicked ? orderedChecked : [],
        trimmedOther
      )
    );
  };
  const Control = spec.multiSelect ? Checkbox : Radio;

  return (
    <Stack spacing={0} sx={{ my: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        {spec.question}
      </Typography>
      {spec.options.map(option => (
        <FormControlLabel
          key={option.label}
          disabled={busy}
          sx={{ display: "flex", alignItems: "flex-start", my: 0 }}
          control={
            <Control
              size="small"
              sx={{ py: 0.25 }}
              checked={isChecked(option.label)}
              onChange={() => pick(option.label)}
            />
          }
          label={
            <Box sx={{ py: 0.25 }}>
              <Typography variant="body2">{option.label}</Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
          }
        />
      ))}
      {spec.allowOther && (
        <>
          <FormControlLabel
            disabled={busy}
            sx={{ my: 0 }}
            control={
              <Control
                size="small"
                sx={{ py: 0.25 }}
                checked={isChecked(OTHER)}
                onChange={() => pick(OTHER)}
              />
            }
            label={t("Other", { ns: "Global" })}
          />
          {otherPicked && (
            <TextField
              size="small"
              autoFocus
              disabled={busy}
              placeholder={t("Other answer")}
              value={otherText}
              onChange={event => setOtherText(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") submit();
              }}
              sx={{ ml: 4, mb: 0.5 }}
            />
          )}
        </>
      )}
      <Box sx={{ mt: 0.5 }}>
        <Button
          size="small"
          variant="contained"
          disabled={busy || !canSend}
          onClick={submit}
        >
          {t("Send", { ns: "Global" })}
        </Button>
      </Box>
    </Stack>
  );
}
